import { useEffect, useRef, useState } from 'react'
import { Button } from '@/shared/ui/button'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card'
import { useTranslation } from 'react-i18next'
import { apiClient } from '@/api/client'
import { errorToUserMessage, mapAxiosError, retryAfterMinutes } from '@/api/errors'
import { Fingerprint, ArrowLeft, KeyRound, Loader2 } from 'lucide-react'

// ── WebAuthn helpers ─────────────────────────────────────────────────────────

function base64UrlToBuffer(base64url: string): ArrayBuffer {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/')
  const pad = base64.length % 4 === 0 ? '' : '='.repeat(4 - (base64.length % 4))
  const binary = atob(base64 + pad)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes.buffer
}

function bufferToBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

// ── Component ───────────────────────────────────────────────────────────────

type MfaStep = 'verifying' | 'fallback' | 'recovery'

interface MfaChallengeProps {
  mfaToken: string
  mfaMethods: string[]
  onSuccess: (data: any) => void
  onCancel?: () => void
}

export function MfaChallenge({ mfaToken, mfaMethods, onSuccess, onCancel }: MfaChallengeProps) {
  const hasPasskey = mfaMethods.includes('passkey')
  const hasTotp = mfaMethods.includes('totp')

  // Always show method picker first — lets users see the recovery code option
  // immediately rather than having to wait for a FaceID/passkey failure
  const { t } = useTranslation()
  const [step, setStep] = useState<MfaStep>('fallback')
  const [error, setError] = useState<string | null>(null)
  const [passkeyLoading, setPasskeyLoading] = useState(false)
  const [totpCode, setTotpCode] = useState('')
  const [recoveryCode, setRecoveryCode] = useState('')
  const [mfaLoading, setMfaLoading] = useState(false)
  const autoTriggered = useRef(false)

  /**
   * F90 — the SECOND_FACTOR cooldown is its own counter with its own copy, and it
   * undersold the same 30-minute lockout as "a few minutes". Worse, it did so by
   * echoing the server's raw English sentence, so a non-English user read English.
   *
   * Both problems have one fix: branch on the refusal code (the pattern
   * `api/errors.ts` documents for a refusal needing feature-specific copy) and
   * render our own localized string from `retryAfterSeconds`. Anything else stays
   * on `errorToUserMessage`, so "Invalid TOTP code" is untouched.
   */
  const mfaErrorMessage = (err: unknown): string => {
    const domainErr = mapAxiosError(err)
    if (domainErr.serverCode !== 'LOGIN_COOLDOWN') return errorToUserMessage(err, t)
    const minutes = retryAfterMinutes(domainErr)
    return minutes === undefined
      ? t('auth.mfa.tooManyCodes')
      : t('auth.mfa.tooManyCodesIn', { count: minutes })
  }

  const attemptPasskey = async (): Promise<boolean> => {
    try {
      const { data: options } = await apiClient.post('/auth/passkey/login/options', {})

      const publicKey: PublicKeyCredentialRequestOptions = {
        ...options,
        challenge: base64UrlToBuffer(options.challenge),
        allowCredentials: (options.allowCredentials || []).map((c: any) => ({
          ...c,
          id: base64UrlToBuffer(c.id),
        })),
      }

      const credential = (await navigator.credentials.get({ publicKey })) as PublicKeyCredential
      if (!credential) return false
      const response = credential.response as AuthenticatorAssertionResponse

      const { data } = await apiClient.post('/auth/passkey/login/verify', {
        credentialId: bufferToBase64Url(credential.rawId),
        clientDataJson: bufferToBase64Url(response.clientDataJSON),
        authenticatorData: bufferToBase64Url(response.authenticatorData),
        signature: bufferToBase64Url(response.signature),
      })
      onSuccess(data)
      return true
    } catch {
      return false
    }
  }

  // Auto-trigger the passkey prompt silently on first render when available
  useEffect(() => {
    if (step === 'verifying' && hasPasskey && !autoTriggered.current) {
      autoTriggered.current = true
      setPasskeyLoading(true)
      attemptPasskey().then(success => {
        setPasskeyLoading(false)
        if (!success) setStep('fallback')
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleTotpVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setMfaLoading(true)
    try {
      const { data } = await apiClient.post('/auth/mfa/verify', {
        mfaToken: mfaToken,
        totpCode: totpCode,
      })
      onSuccess(data)
    } catch (err: any) {
      setError(mfaErrorMessage(err))
    } finally {
      setMfaLoading(false)
    }
  }

  const handleRecoveryVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setMfaLoading(true)
    try {
      const { data } = await apiClient.post('/auth/mfa/verify', {
        mfaToken: mfaToken,
        recoveryCode: recoveryCode.trim(),
      })
      onSuccess(data)
    } catch (err: any) {
      setError(mfaErrorMessage(err))
    } finally {
      setMfaLoading(false)
    }
  }

  // ── Silent verification screen ────────────────────────────────────────────

  if (step === 'verifying') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="w-full max-w-sm">
          <CardContent className="flex flex-col items-center gap-3 pt-8 pb-8">
            <Loader2 className="animate-spin text-muted-foreground" size={24} />
            <p className="text-sm text-muted-foreground">{t('auth.mfa.checking')}</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  // ── Recovery code entry ───────────────────────────────────────────────────

  if (step === 'recovery') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="w-full max-w-sm">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">{t('auth.mfa.recoveryTitle')}</CardTitle>
            <p className="text-sm text-muted-foreground">{t('auth.mfa.recoveryIntro')}</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleRecoveryVerify} className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="recovery-code">{t('auth.mfa.recoveryLabel')}</Label>
                {/*
                  F71 — a recovery code is lowercase hex, and a phone keyboard
                  helps by capitalising the first character, autocorrecting the
                  rest and underlining the lot in red. The server now compares
                  case-insensitively, but the keyboard should stop fighting the
                  user in the first place: nothing here is a word.
                */}
                <Input
                  id="recovery-code"
                  type="text"
                  autoFocus
                  autoCapitalize="none"
                  autoCorrect="off"
                  autoComplete="off"
                  spellCheck={false}
                  value={recoveryCode}
                  onChange={e => setRecoveryCode(e.target.value)}
                  placeholder={t('auth.mfa.recoveryPlaceholder')}
                  className="text-center font-mono text-lg tracking-wider"
                />
              </div>

              {error && <p className="text-sm text-destructive">{error}</p>}

              <Button
                type="submit"
                disabled={recoveryCode.trim().length < 6 || mfaLoading}
                className="w-full"
              >
                {mfaLoading ? 'Verifying…' : 'Verify'}
              </Button>

              <button
                type="button"
                onClick={() => {
                  setStep('fallback')
                  setError(null)
                  setRecoveryCode('')
                }}
                className="text-sm text-muted-foreground hover:underline flex items-center gap-1 justify-center"
              >
                <ArrowLeft size={14} />
                {t('auth.mfa.recoveryBack')}
              </button>
            </form>
          </CardContent>
        </Card>
      </div>
    )
  }

  // ── Fallback screen (TOTP and/or retry the passkey and/or recovery) ──────

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">{t('auth.mfa.title')}</CardTitle>
          <p className="text-sm text-muted-foreground">
            {hasTotp ? t('auth.mfa.totpPrompt') : t('auth.mfa.choosePrompt')}
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleTotpVerify} className="flex flex-col gap-4">
            {hasTotp && (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="totp-code">{t('auth.mfa.codeLabel')}</Label>
                <Input
                  id="totp-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  value={totpCode}
                  onChange={e => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="text-center font-mono text-lg tracking-[0.3em]"
                  maxLength={6}
                />
              </div>
            )}

            {error && <p className="text-sm text-destructive">{error}</p>}

            {hasTotp && (
              <Button
                type="submit"
                disabled={totpCode.length !== 6 || mfaLoading}
                className="w-full"
              >
                {mfaLoading ? 'Verifying…' : 'Verify'}
              </Button>
            )}

            {hasPasskey && (
              <>
                {hasTotp && (
                  <div className="relative my-1">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-card px-2 text-muted-foreground">{t('auth.mfa.or')}</span>
                    </div>
                  </div>
                )}
                <Button
                  type="button"
                  variant="outline"
                  className="w-full gap-2"
                  disabled={passkeyLoading}
                  onClick={async () => {
                    setError(null)
                    setPasskeyLoading(true)
                    const success = await attemptPasskey()
                    setPasskeyLoading(false)
                    if (!success) setError(t('auth.mfa.passkeyFailed'))
                  }}
                >
                  <Fingerprint size={16} />
                  {passkeyLoading ? t('auth.mfa.passkeyWaiting') : t('auth.mfa.usePasskey')}
                </Button>
              </>
            )}

            <div className="relative my-1">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">{t('auth.mfa.troubleSigningIn')}</span>
              </div>
            </div>

            <Button
              type="button"
              variant="ghost"
              className="w-full gap-2 text-muted-foreground"
              onClick={() => {
                setError(null)
                setStep('recovery')
              }}
            >
              <KeyRound size={16} />
              {t('auth.mfa.useRecoveryCode')}
            </Button>

            {onCancel && (
              <button
                type="button"
                onClick={() => {
                  setError(null)
                  onCancel()
                }}
                className="text-sm text-muted-foreground hover:underline flex items-center gap-1 justify-center"
              >
                <ArrowLeft size={14} />
                {t('auth.mfa.backToLogin')}
              </button>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
